import { useEffect } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { contactsSearchResultsWithTotalCountFetchedAsync } from "../../store/action-creators";
import type { ContactsSearchResultsRequestDto } from "../../types/dto";
import { contactsQueryKeys } from "../queryKeys/queryKeyFactory";

import { contactsSearchWithTotalCountQueryOptions } from "./contactsSearchWithTotalCountQueryOptions";

/**
 * Runs the contacts search as a query and mirrors it into the contacts-search redux slice
 * (request → success / failure), so redux consumers keep working while the query owns fetching and cancellation.
 */
const useContactsSearchWithTotalCountQuery = (dto: ContactsSearchResultsRequestDto, { enabled }: { enabled: boolean }) => {
    const dispatch = useDispatch();
    const query = useQuery({ ...contactsSearchWithTotalCountQueryOptions(dto), enabled });
    const { isFetching, status, data, error } = query;

    useEffect(() => {
        if (isFetching) {
            dispatch(contactsSearchResultsWithTotalCountFetchedAsync.request());
        } else if (status === "success") {
            dispatch(contactsSearchResultsWithTotalCountFetchedAsync.success(data));
        } else if (status === "error") {
            dispatch(contactsSearchResultsWithTotalCountFetchedAsync.failure(error));
        }
    }, [dispatch, isFetching, status, data, error]);

    return query;
};

/** Refetches the mounted contacts search, e.g. after a reveal or export changed what it returns. */
export const useRefetchContactsSearchWithTotalCount = () => {
    const queryClient = useQueryClient();

    return () =>
        queryClient.refetchQueries({ queryKey: contactsQueryKeys.baseTableDataWithTotalCount(), type: "active" });
};

export default useContactsSearchWithTotalCountQuery;
